import React, { useEffect, useRef, useState } from 'react';
import { Volume2 } from 'lucide-react';
import { toast } from 'sonner';
// Phase F7 — global exclusive-audio bus (shared with RaccontoPlayer):
// starting an avatar pauses any playing Racconto, and vice versa.
import { claimAudio, releaseAudio } from '../lib/audioBus';

const SIZES = {
  sm: { btn: 18, icon: 10, bars: [5, 8, 11, 8, 5] },
  md: { btn: 34, icon: 16, bars: [8, 14, 20, 14, 8] },
};

/**
 * Audio Avatar play button (Phase 71) — round gold button layered on top
 * of a creator's photo avatar. While playing it shows an animated gold
 * waveform (WhatsApp-style).
 */
export const AudioAvatarPlayer = ({ src, size = 'md', className = '' }) => {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const handleRef = useRef(null);
  const cfg = SIZES[size] || SIZES.md;

  useEffect(() => () => {
    if (audioRef.current) {
      audioRef.current.pause();
      releaseAudio(handleRef.current);
    }
  }, []);

  if (!src) return null;

  const toggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (playing) {
      audioRef.current?.pause();
      setPlaying(false);
      return;
    }
    const audio = new Audio(src);
    audioRef.current = audio;
    const handle = { pause: () => { try { audio.pause(); } catch { /* noop */ } } };
    handleRef.current = handle;
    claimAudio(handle);
    audio.onended = () => { setPlaying(false); releaseAudio(handle); };
    audio.onpause = () => setPlaying(false);
    audio.onerror = () => {
      setPlaying(false);
      toast.error('Audio non disponibile al momento');
    };
    audio.play().then(() => setPlaying(true)).catch(() => {
      setPlaying(false);
      toast.error('Impossibile riprodurre questo audio sul tuo dispositivo');
    });
  };

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={toggle}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggle(e); }}
      className={`gold-gradient flex cursor-pointer items-center justify-center rounded-full border border-black/30 text-black shadow-md transition-transform hover:scale-110 ${playing ? 'audio-avatar-playing' : ''} ${className}`}
      style={{ width: cfg.btn, height: cfg.btn }}
      aria-label={playing ? 'Ferma audio avatar' : 'Ascolta audio avatar'}
      data-testid="audio-avatar-play-btn"
    >
      {playing ? (
        <span className="flex items-end gap-[2px] text-black" data-testid="audio-avatar-wave" style={{ height: cfg.bars[2] }}>
          {cfg.bars.map((h, i) => (
            <span
              key={i}
              className="audio-wave-bar"
              style={{ height: h, animationDelay: `${i * 0.12}s`, width: size === 'sm' ? 1.5 : 2.5 }}
            />
          ))}
        </span>
      ) : (
        <Volume2 style={{ width: cfg.icon, height: cfg.icon }} strokeWidth={2.4} />
      )}
    </span>
  );
};

export default AudioAvatarPlayer;
